import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export interface TradingPerformanceChartPoint {
  period: string;
  periodLabel: string;
  gains: number;
  losses: number;
  pnl: number;
  tradesCount: number;
  /** P&L cumulé (optionnel, pour courbe d'évolution) */
  cumulativePnl?: number;
}

interface TradingPerformanceChartProps {
  data: TradingPerformanceChartPoint[];
  /** Libellés pour la légende / tooltip (optionnel) */
  labels?: {
    gains?: string;
    losses?: string;
    pnl?: string;
    trades?: string;
    cumulativePnl?: string;
  };
}

const defaultLabels = {
  gains: "Gains",
  losses: "Pertes",
  pnl: "P&L net",
  trades: "Trades",
  cumulativePnl: "P&L cumulé",
};

export const TradingPerformanceChart = ({
  data,
  labels: customLabels,
}: TradingPerformanceChartProps) => {
  const labels = { ...defaultLabels, ...customLabels };
  const hasCumulative = data.some((d) => d.cumulativePnl !== undefined);

  const xTickInterval =
    data.length <= 8 ? 0 : Math.max(0, Math.floor(data.length / 10));

  return (
    <div className="h-80 w-full min-h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 16, right: 16, left: 12, bottom: 56 }}>
          <defs>
            <linearGradient id="gainsBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
              <stop offset="100%" stopColor="#059669" stopOpacity={0.85} />
            </linearGradient>
            <linearGradient id="lossesBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f87171" stopOpacity={1} />
              <stop offset="100%" stopColor="#dc2626" stopOpacity={0.85} />
            </linearGradient>
            <linearGradient id="pnlLineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity={1} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(255,255,255,0.06)"
            vertical={false}
          />
          <XAxis
            dataKey="periodLabel"
            stroke="#94a3b8"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
            angle={data.length > 8 ? -40 : 0}
            textAnchor={data.length > 8 ? "end" : "middle"}
            interval={xTickInterval}
          />
          <YAxis
            yAxisId="left"
            stroke="#94a3b8"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            width={48}
            tickFormatter={(v) =>
              `${v >= 0 ? "" : "-"}$${Math.abs(v).toLocaleString()}`
            }
          />
          {hasCumulative && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#64748b"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10 }}
              width={48}
              tickFormatter={(v) =>
                `${v >= 0 ? "+" : ""}${v.toLocaleString()} $`
              }
            />
          )}
          <Tooltip
            contentStyle={{
              background: "rgba(15,23,42,0.97)",
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#fff",
              boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
            }}
            labelStyle={{ color: "#e2e8f0", marginBottom: 6 }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length || !label) return null;
              const row = payload[0]?.payload as TradingPerformanceChartPoint;
              return (
                <div className="space-y-1.5 py-1">
                  <p className="font-medium text-white/95 border-b border-white/10 pb-1.5">
                    {label}
                  </p>
                  <p className="text-emerald-400 text-xs">
                    {labels.gains}: +{row.gains.toFixed(2)} $
                  </p>
                  <p className="text-rose-400 text-xs">
                    {labels.losses}: -{row.losses.toFixed(2)} $
                  </p>
                  <p
                    className={`text-xs font-medium ${
                      row.pnl >= 0 ? "text-cyan-400" : "text-rose-400"
                    }`}>
                    {labels.pnl}: {row.pnl >= 0 ? "+" : ""}
                    {row.pnl.toFixed(2)} $
                  </p>
                  <p className="text-white/60 text-xs">
                    {labels.trades}: {row.tradesCount}
                  </p>
                  {row.cumulativePnl !== undefined && (
                    <p className="text-violet-400/90 text-xs pt-1 border-t border-white/10">
                      P&L cumulé: {row.cumulativePnl >= 0 ? "+" : ""}
                      {row.cumulativePnl.toFixed(2)} $
                    </p>
                  )}
                </div>
              );
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            layout="horizontal"
            verticalAlign="bottom"
            formatter={(value) => (
              <span className="text-white/80">{value}</span>
            )}
          />
          <ReferenceLine
            yAxisId="left"
            y={0}
            stroke="rgba(255,255,255,0.2)"
            strokeDasharray="2 2"
          />
          <Bar
            yAxisId="left"
            dataKey="gains"
            name={labels.gains}
            fill="url(#gainsBar)"
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          />
          <Bar
            yAxisId="left"
            dataKey="losses"
            name={labels.losses}
            fill="url(#lossesBar)"
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="pnl"
            name={labels.pnl}
            stroke="#22d3ee"
            strokeWidth={2}
            dot={{ fill: "#0f172a", stroke: "#22d3ee", strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: "#0f172a", strokeWidth: 2 }}
          />
          {hasCumulative && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cumulativePnl"
              name={labels.cumulativePnl}
              stroke="#a78bfa"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 4 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
